import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import MicroPlayer from "../../common-components/micro-player/micro-player";
import axios from "axios";
import { genreList, getSubGenres } from "../../../utils/genres";
import { useGenreSelection } from "../../../hooks/useGenreSelection";
import {
  Trash2,
  Edit3,
  RotateCcw,
  Info,
  Plus,
  UploadCloud,
  Disc,
  AlertCircle,
} from "lucide-react";
import { isoLangs } from "../../../utils/languages";
import { API_URL } from "../../../utils/url";

// shadcn/ui components
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { Progress } from "../../ui/progress";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from "../../ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Badge } from "../../ui/badge";

export default function TrackList({ albumId, tracks, setTracks, apiPrefix }) {
  const [songEditVisible, setSongEditVisible] = useState(false);
  const [currentEditTrack, setCurrentEditTrack] = useState({});
  const [currentUploadingTrack, setCurrentUploadingTrack] = useState([]);
  const [currentDeleteTrack, setCurrentDeleteTrack] = useState(null);
  const [trackUploadProgress, setTrackUploadProgress] = useState({});

  const form = useForm({
    defaultValues: {
      title: "",
      primaryArtist: "",
      secondaryArtist: "",
      composer: "",
      lyricist: "",
      explicit: false,
      mainGenre: "",
      subGenre: "",
      language: "",
      ISRC: "",
    },
  });

  const {
    selectedMainGenre,
    availableSubGenres,
    handleMainGenreChange,
    setSelectedMainGenre,
    setAvailableSubGenres,
  } = useGenreSelection({
    form,
  });

  const handleFinish = (values) => {
    Object.keys(values).forEach((key) => {
      if (
        values[key] === "" ||
        values[key] === null ||
        values[key] === undefined
      ) {
        values[key] = "__delete__";
      }
    });

    axios
      .patch(apiPrefix + "/songs/" + currentEditTrack._id, { ...values })
      .then(() => {
        console.log("Album updated");
        toast.success("Track edited successfully.");
        setTracks((existingSongs) => {
          return existingSongs.map((track) => {
            if (track._id === currentEditTrack._id) {
              Object.keys(values).forEach((key) => {
                if (!values[key]) return;
                if (values[key] === "__delete__") track[key] = null;
                else track[key] = values[key];
              });
            }
            return track;
          });
        });
        closeSongEditor();
      })
      .catch((error) => {
        toast.error("Error editing track: " + error.response.data.error);
      });
    console.log("values: ", values);
  };

  useEffect(() => {
    form.reset({
      title: currentEditTrack.title || "",
      primaryArtist: currentEditTrack.primaryArtist || "",
      secondaryArtist: currentEditTrack.secondaryArtist || "",
      composer: currentEditTrack.composer || "",
      lyricist: currentEditTrack.lyricist || "",
      explicit: currentEditTrack.explicit || false,
      mainGenre: currentEditTrack.mainGenre || "",
      subGenre: currentEditTrack.subGenre || "",
      language: currentEditTrack.language || "",
      ISRC: currentEditTrack.ISRC || "",
    });
    // eslint-disable-next-line
  }, [currentEditTrack, form]);

  const addNewTrack = () => {
    axios
      .post(apiPrefix + "/songs", { albumId })
      .then((response) => {
        const song = response.data.data.songInfo;
        setTracks((existingSongs) => [...existingSongs, song]);
      })
      .catch((error) => {
        toast.error("Error creating track: " + error.response.data.error);
      });
  };

  const deleteSong = (songId) => {
    setCurrentDeleteTrack(songId);
    axios
      .delete("/songs/" + songId)
      .then(() => {
        setTracks((existingSongs) =>
          existingSongs.filter((s) => s._id !== songId)
        );
      })
      .catch((error) => {
        toast.error("Error deleting track: " + error.response.data.error);
      })
      .finally(() => {
        setCurrentDeleteTrack(null);
      });
  };

  const closeSongEditor = () => {
    setSongEditVisible(false);
  };

  const openSongEditor = (track) => {
    setCurrentEditTrack(track);
    setSelectedMainGenre(track.mainGenre || null);
    setAvailableSubGenres(track.mainGenre ? getSubGenres(track.mainGenre) : []);
    setSongEditVisible(true);
  };

  function beforeUpload(file, track) {
    uploadTrack(file, track);
    return false;
  }

  const uploadTrack = async (file, track) => {
    const supportedTypes = [
      "audio/wav",
      "audio/mpeg",
      "audio/flac",
      "audio/x-wav",
    ];
    const isAudioFile = supportedTypes.includes(file.type);
    if (!isAudioFile) {
      return toast.error("Please upload a MP3, WAV or FLAC file");
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      return toast.error("Master file must smaller than 100MB");
    }

    const formData = new FormData();
    formData.append("masterFilename", file);

    setCurrentUploadingTrack((currentUploadingTrack) => {
      const currentUploadingTrackCopy = [...currentUploadingTrack];
      currentUploadingTrackCopy.push(track._id);
      return currentUploadingTrackCopy;
    });

    const newTrack = { ...track };

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader("content-length") ||
            progressEvent.target.getResponseHeader(
              "x-decompressed-content-length"
            );
        if (totalLength !== null) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / totalLength
          );
          setTrackUploadProgress((progress) => {
            const copy = { ...progress };
            copy[newTrack._id] = percentage;
            return copy;
          });
        }
      },
    };
    axios
      .put(
        API_URL + apiPrefix + "/songs/" + newTrack._id + "/master",
        formData,
        config
      )
      .then((response) => {
        const data = response.data.data;
        newTrack.masterUrl = data.signedUrl;
        toast.success("File uploaded successfully.");
        setTracks((oldTracks) =>
          oldTracks.map((t) => (t._id === newTrack._id ? newTrack : t))
        );
      })
      .catch((error) => {
        toast.error("Error uploading track file: " + error.response.data.error);
      })
      .finally(() => {
        setCurrentUploadingTrack((currentUploadingTrack) =>
          currentUploadingTrack.filter((t) => t !== track._id)
        );
        setTrackUploadProgress((progress) => {
          const copy = { ...progress };
          copy[newTrack._id] = 0;
          return copy;
        });
      });
  };

  const getUploadButtonContent = (track) => {
    // 1. Uploading State
    if (currentUploadingTrack.includes(track._id))
      return (
        <div className="flex items-center gap-2 w-full max-w-[200px]">
          <Progress
            value={trackUploadProgress[track._id]}
            className="h-2 flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">
            {trackUploadProgress[track._id]}%
          </span>
        </div>
      );

    // 2. Has Audio State (Show Player + Change Button)
    if (track.masterUrl)
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <MicroPlayer source={track} />
          </div>
          <div className="relative">
            <input
              type="file"
              accept="audio/*"
              className="absolute inset-0 opacity-0 cursor-pointer w-full"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  uploadTrack(file, track);
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-2 text-muted-foreground hover:text-primary px-2"
            >
              <RotateCcw className="w-3 h-3" />{" "}
              <span className="hidden md:inline">Change</span>
            </Button>
          </div>
        </div>
      );

    // 3. Empty State
    return (
      <div className="relative inline-block">
        <input
          type="file"
          accept="audio/*"
          className="absolute inset-0 opacity-0 cursor-pointer w-full"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              uploadTrack(file, track);
            }
          }}
        />
        <Button variant="outline" size="sm" className="gap-2 h-8 bg-background">
          <UploadCloud className="w-4 h-4" /> Upload
        </Button>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <Drawer open={songEditVisible} onOpenChange={setSongEditVisible}>
        <DrawerContent className="mx-auto max-w-4xl max-h-[85vh] flex flex-col">
          <DrawerHeader className="shrink-0">
            <DrawerTitle>Edit Song Details</DrawerTitle>
          </DrawerHeader>
          <div className="px-6 pb-6 overflow-y-auto flex-1">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleFinish)}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    rules={{
                      required: "Please enter track title",
                      maxLength: {
                        value: 150,
                        message: "Track title too long",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Track Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Track Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="ISRC"
                    rules={{
                      minLength: {
                        value: 12,
                        message: "Please enter a valid ISRC",
                      },
                      maxLength: {
                        value: 12,
                        message: "Please enter a valid ISRC",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          ISRC Code
                          <Popover>
                            <PopoverTrigger>
                              <Info className="w-4 h-4 text-muted-foreground cursor-pointer hover:text-foreground" />
                            </PopoverTrigger>
                            <PopoverContent className="w-64">
                              <p className="text-sm">
                                International Standard Recording Code. Leave
                                blank to auto-assign a free ISRC.
                              </p>
                            </PopoverContent>
                          </Popover>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Optional" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="mainGenre"
                    rules={{ required: "Please enter main genre" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Main Genre</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            handleMainGenreChange(value);
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Genre" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {genreList.map((genre) => (
                              <SelectItem key={genre} value={genre}>
                                {genre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subGenre"
                    rules={{ required: "Please enter sub genre" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sub Genre</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedMainGenre}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Sub Genre" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {availableSubGenres.map((subGenre) => (
                              <SelectItem key={subGenre} value={subGenre}>
                                {subGenre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="primaryArtist"
                    rules={{ required: "Primary Artist is Required!" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Artist</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="secondaryArtist"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Artist</FormLabel>
                        <FormControl>
                          <Input placeholder="Optional" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="composer"
                    rules={{ required: "Composer is Required!" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Composer</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Enter composer's original first and last name.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lyricist"
                    rules={{ required: "Lyricist is Required!" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lyricist</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="language"
                  rules={{ required: "Language of release is Required!" }}
                  render={({ field }) => (
                    <FormItem className="w-full md:w-1/2">
                      <FormLabel>Language</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Language" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(isoLangs).map((language) => (
                            <SelectItem
                              key={language.name}
                              value={language.name}
                            >
                              {language.name} ({language.nativeName})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="explicit"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Explicit Content</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Check this if the song contains explicit lyrics.
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
          <DrawerFooter className="border-t pt-4 shrink-0">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeSongEditor}>
                Cancel
              </Button>
              <Button onClick={form.handleSubmit(handleFinish)}>
                Save Track Details
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <div className="fade-in space-y-4">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px] text-center">#</TableHead>
                <TableHead className="w-[300px]">Track Details</TableHead>
                <TableHead className="min-w-[200px]">Audio File</TableHead>
                <TableHead className="hidden md:table-cell">ISRC</TableHead>
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No tracks added. Click "Add Track" to begin.
                  </TableCell>
                </TableRow>
              ) : (
                tracks.map((track, index) => (
                  <TableRow
                    key={track._id}
                    className="group hover:bg-muted/50 transition-colors"
                  >
                    <TableCell className="text-center font-medium text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 shrink-0 rounded bg-muted flex items-center justify-center">
                          <Disc className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate flex items-center gap-2">
                            {track.title || (
                              <span className="text-muted-foreground italic">
                                Untitled Track
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {track.primaryArtist || "Unknown Artist"}
                          </div>
                          {(!track.title || !track.primaryArtist) && (
                            <div className="flex mt-1">
                              <Badge
                                variant="outline"
                                className="h-5 px-1.5 text-[10px] border-destructive/50 text-destructive bg-destructive/10 hover:bg-destructive/20 cursor-pointer transition-colors"
                                onClick={() => openSongEditor(track)}
                              >
                                <AlertCircle className="w-3 h-3 mr-1" />{" "}
                                Complete Info
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getUploadButtonContent(track)}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
                      {track.ISRC || <span className="opacity-50">-</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => openSongEditor(track)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit Details</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteSong(track._id)}
                              disabled={currentDeleteTrack === track._id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete Track</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Button
          variant="outline"
          className="w-full h-12 border-dashed border-2 hover:bg-muted/50 hover:border-primary/50 text-muted-foreground hover:text-primary transition-all"
          onClick={addNewTrack}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Track
        </Button>
      </div>
    </TooltipProvider>
  );
}
